import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowLeft, BookmarkX, ChefHat, Clock, Utensils,
  Star, Trash2, Search, BookOpen,
} from 'lucide-react';
import { useAppStore, SavedRecipe } from '@/hooks/useAppStore';
import { ROUTE_PATHS, mealTypeIcon, budgetLabel, budgetColor } from '@/lib/index';
import { useAuth } from '@/hooks/useAuth';

// ── Rating helpers (same key as Recipe.tsx) ───────────────────────────────────
const RATINGS_KEY = 'flyerfeast-ratings';
function getMealRating(id: string): number {
  try {
    return (JSON.parse(localStorage.getItem(RATINGS_KEY) ?? '{}') as Record<string, number>)[id] ?? 0;
  } catch { return 0; }
}

function StarDisplay({ id }: { id: string }) {
  const rating = getMealRating(id);
  if (rating === 0) return null;
  return (
    <div className="flex items-center gap-0.5">
      {[1,2,3,4,5].map(s => (
        <Star key={s} className={`w-3 h-3 ${s <= rating ? 'fill-yellow-400 text-yellow-400' : 'fill-none text-muted-foreground/30'}`} />
      ))}
    </div>
  );
}

function RecipeCard({ recipe, onView, onUnsave }: { recipe: SavedRecipe; onView: () => void; onUnsave: () => void }) {
  const [confirmDelete, setConfirmDelete] = useState(false);
  const label = budgetLabel(recipe.per_serving ?? 0);
  const colorCls = budgetColor(label);
  const savedDate = new Date(recipe.savedAt).toLocaleDateString('en-CA', { month: 'short', day: 'numeric' });

  return (
    <motion.div
      layout
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.9 }}
      className="bg-card border border-border rounded-2xl overflow-hidden"
    >
      {/* Colour bar based on meal type */}
      <div
        className="h-1.5 w-full"
        style={{ background: recipe.meal_type?.toLowerCase().includes('dinner')
          ? 'linear-gradient(90deg,#16a34a,#22d3ee)'
          : recipe.meal_type?.toLowerCase().includes('lunch')
          ? 'linear-gradient(90deg,#f59e0b,#f97316)'
          : 'linear-gradient(90deg,#8b5cf6,#ec4899)' }}
      />

      <div className="p-4">
        {/* Header row */}
        <div className="flex items-start justify-between gap-2 mb-2">
          <div className="flex items-center gap-2 min-w-0">
            <span className="text-xl shrink-0">{mealTypeIcon(recipe.meal_type ?? '')}</span>
            <div className="min-w-0">
              <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider truncate">
                {recipe.meal_type} · {recipe.day}
              </p>
              <h3 className="font-black text-foreground text-sm leading-snug line-clamp-2">
                {recipe.name}
              </h3>
            </div>
          </div>
          <button
            onClick={() => setConfirmDelete(true)}
            className="shrink-0 w-7 h-7 rounded-full bg-muted hover:bg-red-500/15 hover:text-red-400 flex items-center justify-center transition-colors"
          >
            <BookmarkX className="w-3.5 h-3.5" />
          </button>
        </div>

        {/* Tagline */}
        {recipe.tagline && (
          <p className="text-xs text-muted-foreground line-clamp-2 mb-3">{recipe.tagline}</p>
        )}

        {/* Meta chips */}
        <div className="flex flex-wrap items-center gap-1.5 mb-3">
          <span className="flex items-center gap-1 text-[11px] text-muted-foreground">
            <Clock className="w-3 h-3" />{recipe.cook_time}
          </span>
          <span className="flex items-center gap-1 text-[11px] text-muted-foreground">
            <Utensils className="w-3 h-3" />{recipe.method}
          </span>
          <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${colorCls}`}>
            ${(recipe.per_serving ?? 0).toFixed(2)}/srv
          </span>
        </div>

        <StarDisplay id={recipe.id} />

        {/* Footer */}
        <div className="flex items-center justify-between mt-3 pt-3 border-t border-border/50">
          <span className="text-[10px] text-muted-foreground">
            {recipe.sourcePlan ? `${recipe.sourcePlan} · ` : ''}Saved {savedDate}
          </span>
          <button
            onClick={onView}
            className="flex items-center gap-1 text-xs font-bold text-primary hover:underline"
          >
            <BookOpen className="w-3 h-3" /> View Recipe
          </button>
        </div>
      </div>

      {/* Delete confirmation overlay */}
      <AnimatePresence>
        {confirmDelete && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="absolute inset-0 bg-background/90 backdrop-blur-sm rounded-2xl flex flex-col items-center justify-center gap-3 p-4"
          >
            <Trash2 className="w-8 h-8 text-red-400" />
            <p className="text-sm font-bold text-foreground text-center">Remove from saved?</p>
            <div className="flex gap-2">
              <button onClick={() => setConfirmDelete(false)}
                className="px-4 py-1.5 rounded-xl bg-muted text-sm font-semibold">Cancel</button>
              <button onClick={onUnsave}
                className="px-4 py-1.5 rounded-xl bg-red-500 text-white text-sm font-bold">Remove</button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

export default function SavedRecipesPage() {
  const navigate = useNavigate();
  const { user }  = useAuth();
  const { savedRecipes, unsaveRecipe, mealPlan } = useAppStore();
  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState<string>('All');

  const mealTypes = ['All', ...Array.from(new Set(savedRecipes.map(r => r.meal_type).filter(Boolean)))];

  const displayed = savedRecipes.filter(r => {
    const matchType = filterType === 'All' || r.meal_type === filterType;
    const matchSearch = !search.trim() || r.name.toLowerCase().includes(search.toLowerCase());
    return matchType && matchSearch;
  });

  const handleView = (recipe: SavedRecipe) => {
    // If the recipe's meal is still in the current plan, navigate directly
    if (mealPlan?.meals.some(m => m.id === recipe.id)) {
      navigate(`/recipe/${recipe.id}`);
    } else {
      navigate(`/recipe/${recipe.id}`);
    }
  };

  // Auth gate
  if (!user) {
    return (
      <div className="min-h-screen bg-background flex flex-col pt-12">
        <div className="sticky top-12 z-10 bg-background/95 backdrop-blur border-b border-border">
          <div className="container mx-auto max-w-2xl px-4 py-3 flex items-center gap-3">
            <button onClick={() => navigate(-1)}
              className="w-9 h-9 rounded-xl bg-muted flex items-center justify-center">
              <ArrowLeft className="w-4 h-4" />
            </button>
            <h1 className="font-black text-foreground text-base">My Recipes</h1>
          </div>
        </div>
        <div className="flex-1 flex flex-col items-center justify-center gap-5 px-6 text-center">
          <div className="w-16 h-16 rounded-2xl bg-primary/10 border border-primary/25 flex items-center justify-center">
            <ChefHat className="w-8 h-8 text-primary" />
          </div>
          <div>
            <h2 className="font-black text-foreground text-xl mb-2">Sign in to Save Recipes</h2>
            <p className="text-sm text-muted-foreground max-w-xs">
              Create a free account to save your favourite recipes and access them anytime.
            </p>
          </div>
          <button
            onClick={() => navigate(ROUTE_PATHS.HOME)}
            className="px-6 py-3 rounded-2xl bg-primary text-white font-bold hover:opacity-90 transition-opacity shadow-lg shadow-primary/30">
            Sign In / Sign Up
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pt-12 pb-28">

      {/* Header */}
      <div className="sticky top-12 z-10 bg-background/95 backdrop-blur border-b border-border">
        <div className="container mx-auto max-w-2xl px-4 py-3 flex items-center gap-3">
          <button onClick={() => navigate(-1)}
            className="w-9 h-9 rounded-xl bg-muted hover:bg-muted/80 flex items-center justify-center transition-colors shrink-0">
            <ArrowLeft className="w-4 h-4" />
          </button>
          <div className="flex-1 min-w-0">
            <h1 className="font-black text-foreground text-base">My Recipes</h1>
            <p className="text-xs text-muted-foreground">
              {savedRecipes.length} saved recipe{savedRecipes.length !== 1 ? 's' : ''}
            </p>
          </div>
        </div>

        {savedRecipes.length > 0 && (
          <div className="container mx-auto max-w-2xl px-4 pb-3 space-y-2">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <input
                type="text"
                placeholder="Search saved recipes…"
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="w-full h-9 pl-9 pr-4 rounded-xl bg-muted border border-border text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
              />
            </div>
            {/* Type filter pills */}
            {mealTypes.length > 2 && (
              <div className="flex gap-2 overflow-x-auto scrollbar-hide">
                {mealTypes.map(type => (
                  <button
                    key={type}
                    onClick={() => setFilterType(type)}
                    className={`shrink-0 px-3 py-1 rounded-full text-xs font-semibold border transition-all ${
                      filterType === type
                        ? 'bg-primary text-white border-primary'
                        : 'bg-muted text-muted-foreground border-border hover:opacity-80'
                    }`}>
                    {type}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      <div className="container mx-auto max-w-2xl px-4 pt-5">

        {/* Empty state */}
        {savedRecipes.length === 0 && (
          <div className="flex flex-col items-center justify-center py-24 gap-5 text-center">
            <div className="w-16 h-16 rounded-2xl bg-muted flex items-center justify-center">
              <ChefHat className="w-8 h-8 text-muted-foreground" />
            </div>
            <div>
              <h2 className="font-black text-foreground text-lg mb-1">No Saved Recipes Yet</h2>
              <p className="text-sm text-muted-foreground max-w-xs">
                Tap the bookmark icon on any recipe card to save it here for quick access.
              </p>
            </div>
            <button onClick={() => navigate(ROUTE_PATHS.MEAL_PLAN)}
              className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-primary text-white font-bold text-sm hover:opacity-90 transition-opacity shadow-lg shadow-primary/30">
              <ChefHat className="w-4 h-4" /> Browse Meal Plan
            </button>
          </div>
        )}

        {/* No search results */}
        {savedRecipes.length > 0 && displayed.length === 0 && (
          <div className="text-center py-16">
            <p className="text-3xl mb-3">🔍</p>
            <p className="font-semibold text-foreground">No recipes match your search</p>
            <button onClick={() => { setSearch(''); setFilterType('All'); }}
              className="text-sm text-primary mt-2">Clear filters</button>
          </div>
        )}

        {/* Recipe grid */}
        {displayed.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 relative">
            <AnimatePresence mode="popLayout">
              {displayed.map(recipe => (
                <RecipeCard
                  key={recipe.id}
                  recipe={recipe}
                  onView={() => handleView(recipe)}
                  onUnsave={() => unsaveRecipe(recipe.id)}
                />
              ))}
            </AnimatePresence>
          </div>
        )}
      </div>
    </div>
  );
}
